'use client'
import clsx from 'clsx'
import {
    forwardRef,
    useEffect,
    useRef,
    useState,
    type AllHTMLAttributes,
    type CSSProperties,
    type ElementType,
} from 'react'
import { onTrasitionEnd } from '@/lib/transition'

type DrawerDirection = 'horizontal' | 'vertical'

type DrawerProps = AllHTMLAttributes<HTMLElement> & {
    open?: boolean
    as?: ElementType
    direction?: DrawerDirection
    openClass?: string
    onOpen?: () => void
    closedClass?: string
    onClose?: () => void
}

const Drawer = forwardRef<HTMLElement, DrawerProps>((props, ref) => {
    const {
        children,
        className,
        open = true,
        as: Component = 'div',
        direction = 'vertical',
        closedClass,
        openClass,
        onOpen,
        onClose,
        ...otherProps
    } = props
    const drawerRef = useRef<HTMLElement>(null)

    const initialStyle = direction === 'vertical' ? { height: 0, display: 'none' } : { width: 0, display: 'none' }
    const [currentStyle, setCurrentStyle] = useState<CSSProperties>(open ? {} : initialStyle)
    const [isOpen, setIsOpen] = useState(open)

    useEffect(() => {
        if (typeof ref === 'function') {
            ref(drawerRef.current)
        } else if (ref) {
            ref.current = drawerRef.current
        }
    }, [ref, drawerRef])

    useEffect(() => {
        if (!drawerRef.current) return

        if (open && !isOpen) {
            setCurrentStyle(direction === 'vertical' ? { height: 0 } : { width: 0 })

            setTimeout(() => {
                if (!drawerRef.current) return

                onTrasitionEnd(drawerRef.current, () => {
                    setCurrentStyle({})
                    setIsOpen(true)
                    if (onOpen) onOpen()
                })

                setCurrentStyle(
                    direction === 'vertical'
                        ? { height: drawerRef.current.scrollHeight }
                        : { width: drawerRef.current.scrollWidth },
                )
            }, 10)
        } else if (!open && isOpen) {
            onTrasitionEnd(drawerRef.current, () => {
                setCurrentStyle(
                    direction === 'vertical' ? { height: 0, display: 'none' } : { width: 0, display: 'none' },
                )
                setIsOpen(false)
                if (onClose) onClose()
            })

            setCurrentStyle(
                direction === 'vertical'
                    ? { height: drawerRef.current.scrollHeight }
                    : { width: drawerRef.current.scrollWidth },
            )

            setTimeout(() => {
                setCurrentStyle(direction === 'vertical' ? { height: 0 } : { width: 0 })
            }, 10)
        }
    }, [open, direction, onOpen, onClose, isOpen])

    return (
        <Component
            className={clsx('overflow-hidden transition-all', open ? openClass : closedClass, className)}
            aria-hidden={!isOpen}
            style={currentStyle}
            ref={drawerRef}
            {...otherProps}
        >
            {children}
        </Component>
    )
})

Drawer.displayName = 'Drawer'

export { Drawer }
